import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { WeightSlider } from '@/components/ui/Form/WeightSlider'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { FraudRule } from '@/services/mockRules'
import { Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'

interface ThresholdConfiguratorProps {
  threshold: number
  onThresholdChange: (threshold: number) => void
  rules: FraudRule[]
}

export function ThresholdConfigurator({ 
  threshold, 
  onThresholdChange, 
  rules 
}: ThresholdConfiguratorProps) {
  // Calculate impact based on threshold
  const calculateImpact = () => {
    // Simulate historical data impact
    const totalLeads = 975
    const fraudLeads = 312
    const legitimateLeads = 663

    // Simplified calculation - in reality this would be more complex
    const autoApprove = Math.floor(totalLeads * (30 / 100))
    const manualReview = Math.floor(totalLeads * ((threshold - 30) / 100))
    const autoBlock = totalLeads - autoApprove - manualReview

    const wouldBlockFraud = Math.min(fraudLeads, autoBlock)
    const wouldBlockLegitimate = Math.max(0, autoBlock - fraudLeads)

    return {
      autoApprove,
      manualReview,
      autoBlock,
      wouldBlockFraud,
      wouldBlockLegitimate,
      accuracy: ((wouldBlockFraud + (legitimateLeads - wouldBlockLegitimate)) / totalLeads) * 100
    }
  }

  const impact = calculateImpact()
  const activeRules = rules.filter(rule => rule.enabled)
  const maxPossibleScore = activeRules.reduce((sum, rule) => sum + rule.weight, 0)

  const thresholdMarks = [
    { value: 0, label: 'Allow All', color: 'text-green-600' },
    { value: 30, label: 'Low Risk', color: 'text-green-600' },
    { value: 50, label: 'Medium', color: 'text-yellow-600' },
    { value: 70, label: 'High Risk', color: 'text-red-600' },
    { value: 90, label: 'Critical', color: 'text-red-800' }
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Blocking Threshold Configuration
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Set the risk score threshold for automatic lead blocking. 
            Leads scoring above this threshold will be automatically rejected.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Threshold Slider */}
          <div className="space-y-4">
            <WeightSlider
              value={threshold}
              onChange={onThresholdChange}
              label="Blocking Threshold"
              color="risk"
              min={0}
              max={100}
              step={1}
              showMarks
            />
            
            {/* Threshold Markers */}
            <div className="flex justify-between text-xs">
              {thresholdMarks.map((mark) => (
                <div key={mark.value} className="text-center">
                  <div className={`font-medium ${mark.color}`}>
                    {mark.label}
                  </div>
                  <div className="text-muted-foreground">
                    {mark.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Configuration Display */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">Auto-Approve</span>
              </div>
              <div className="text-sm text-green-700">
                Score: 0 - {Math.max(0, threshold - 1)}
              </div>
              <div className="text-xs text-green-600">
                Low risk leads pass through immediately
              </div>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="font-medium text-yellow-800">Manual Review</span>
              </div>
              <div className="text-sm text-yellow-700">
                Score: 30 - {threshold - 1}
              </div>
              <div className="text-xs text-yellow-600">
                Medium risk leads require human review
              </div>
            </div>
            
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="font-medium text-red-800">Auto-Block</span>
              </div>
              <div className="text-sm text-red-700">
                Score: {threshold} - 100
              </div>
              <div className="text-xs text-red-600">
                High risk leads are automatically rejected
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Impact Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Threshold Impact Analysis</CardTitle>
          <p className="text-sm text-muted-foreground">
            Based on historical data from the last 30 days
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Lead Distribution */}
          <div className="space-y-4">
            <h4 className="font-medium">Lead Processing Distribution</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Auto-Approve</span>
                <div className="flex items-center gap-2">
                  <Progress value={(impact.autoApprove / 975) * 100} className="w-24 h-2" />
                  <span className="text-sm font-medium w-12">{impact.autoApprove}</span>
                  <Badge variant="secondary" className="text-xs">
                    {Math.round((impact.autoApprove / 975) * 100)}%
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Manual Review</span>
                <div className="flex items-center gap-2">
                  <Progress value={(impact.manualReview / 975) * 100} className="w-24 h-2" />
                  <span className="text-sm font-medium w-12">{impact.manualReview}</span>
                  <Badge variant="secondary" className="text-xs">
                    {Math.round((impact.manualReview / 975) * 100)}%
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Auto-Block</span>
                <div className="flex items-center gap-2">
                  <Progress value={(impact.autoBlock / 975) * 100} className="w-24 h-2" />
                  <span className="text-sm font-medium w-12">{impact.autoBlock}</span>
                  <Badge variant="secondary" className="text-xs">
                    {Math.round((impact.autoBlock / 975) * 100)}%
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Effectiveness Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-lg font-bold text-green-600">
                {impact.wouldBlockFraud}
              </div>
              <div className="text-xs text-green-700">
                Fraud Blocked
              </div>
            </div>
            
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-lg font-bold text-red-600">
                {impact.wouldBlockLegitimate}
              </div>
              <div className="text-xs text-red-700">
                Legitimate Blocked
              </div>
            </div>
            
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-lg font-bold text-blue-600">
                {Math.round(impact.accuracy)}%
              </div>
              <div className="text-xs text-blue-700">
                Overall Accuracy
              </div>
            </div>
            
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-lg font-bold text-purple-600">
                {maxPossibleScore}
              </div>
              <div className="text-xs text-purple-700">
                Max Possible Score
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium mb-2">Recommendations</h4>
            <div className="space-y-2 text-sm">
              {threshold < 60 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                  <div>
                    <strong>Consider increasing threshold:</strong> Current setting may allow too many fraudulent leads through.
                  </div>
                </div>
              )}
              {threshold > 80 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                  <div>
                    <strong>Consider decreasing threshold:</strong> Current setting may block too many legitimate leads.
                  </div>
                </div>
              )}
              {threshold >= 60 && threshold <= 80 && (
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <div>
                    <strong>Optimal range:</strong> Current threshold provides good balance between fraud detection and legitimate lead approval.
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}